import React, { useState, ChangeEvent, ReactNode, useRef } from 'react'

import Button from '../../../scentregroup-ui/components/form/button'
import Form from '../../../scentregroup-ui/components/form/form'
import Paragraph from '../../../scentregroup-ui/components/paragraph'
import Textfield from '../../../scentregroup-ui/components/form/textfield'
import Row from '../../../scentregroup-ui/components/form/row'
import { SocialButtons } from '../social-buttons'
import PasswordField from '../password-field'

import useAuth0ClientConfig from '../../../lib/auth0/use-auth0-client-config'
import {
  Result,
  createErrorResult,
  createSuccessResult,
} from '../../../lib/types/result'
import { LoginError, Login } from '../../../lib/apiService'
import { createFormValidator } from '../../../lib/formValidation'
import OnboardingForm from '../onboarding-form'
import { isEmptyString, isEmail } from '../../../lib/validators'
import useAnalytics from '../../../lib/analytics/use-analytics'
import { trackContinueLogin, logError } from '../../../lib/analytics/events'
import style from './index.scss'

const EMAIL_IS_EMPTY = 'Please enter a valid email address and try again'
const PASSWORD_IS_INVALID = 'Please enter a valid password and try again'
const getErrorMessages = () => {
  return {
    EMAIL_IS_EMPTY,
    EMAIL_IS_INVALID: EMAIL_IS_EMPTY,
    EMAIL_IS_UNUSED:
      'There isn’t an active Westfield account for this email. Please sign up instead or try another one.',
    EMAIL_HAS_PASSWORD:
      'There is an active Westfield account for this email. Please login instead or try another one.',
    EMAIL_IS_REJECTED: 'The email is not valid. Please try another one.',
    PASSWORD_IS_INVALID,
    PASSWORD_IS_EMPTY: 'Please enter a valid password and try again',
  }
}

const validateEmail = (email: string): Result<string> => {
  const errors = getErrorMessages()

  if (isEmptyString(email)) {
    return createErrorResult(errors.EMAIL_IS_EMPTY)
  }

  return isEmail(email)
    ? createSuccessResult()
    : createErrorResult(errors.EMAIL_IS_INVALID)
}

const validatePassword = (password: string): Result<string> => {
  if (isEmptyString(password)) {
    return createErrorResult(getErrorMessages().PASSWORD_IS_EMPTY)
  }

  return createSuccessResult()
}

export interface Values extends Record<string, unknown> {
  email: string
  password: string
}

export interface CreateNewPasswordValues extends Record<string, unknown> {
  email: string
}

interface Errors {
  email: string
  password: string
}

export interface OnChangeEmail {
  (email: string): void
}

interface OnFailureData extends Values {
  errors: LoginError[]
}
export interface OnFailure {
  (data: OnFailureData): void
}

export interface RenderForgotPasswordLink {
  (className: string): ReactNode
}

export interface RenderSignUpLink {
  (className: string): ReactNode
}

export interface RenderCreateNewPasswordLink {
  (className: string): ReactNode
}

interface Props {
  draftEmail: string
  onChangeEmail: OnChangeEmail
  onFailure: OnFailure
  renderForgotPasswordLink: RenderForgotPasswordLink
  renderSignUpLink: RenderSignUpLink
  renderCreateNewPasswordLink: RenderCreateNewPasswordLink
  login: Login
  googleLogin?: () => void
  appleLogin?: () => void
}

const LoginForm: React.FC<Props> = (props) => {
  const [values, setValues] = useState<Values>({
    email: props.draftEmail,
    password: '',
  })

  const [errors, setErrors] = useState<Errors>({
    email: '',
    password: '',
  })

  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [isBlurValidationEnabled, setIsBlurValidationEnabled] = useState(true)
  const { dataLayer } = useAnalytics()
  const { cookieDomain, consumerUrl } = useAuth0ClientConfig()

  const emailInput = useRef<HTMLInputElement>(null)
  const passwordInput = useRef<HTMLInputElement>(null)

  const validateForm = createFormValidator<Values>(
    values,
    (fieldName, errorValue) => {
      setErrors((state) => ({
        ...state,
        [fieldName]: errorValue,
      }))
      if (errorValue) {
        dataLayer(logError('Login Validation Error', errorValue))
      }
    },
    () => [
      {
        validationFn: validateEmail,
        fieldName: 'email',
        fieldElement: emailInput.current,
      },
      {
        validationFn: validatePassword,
        fieldName: 'password',
        fieldElement: passwordInput.current,
      },
    ]
  )

  const onSubmit = async (e: ChangeEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const validationResult = validateForm()

    if (!validationResult.success) {
      return
    }

    setIsLoggingIn(true)
    const loginResult = await props.login(
      values.email,
      values.password,
      cookieDomain
    )
    setIsLoggingIn(false)

    if (loginResult.success) {
      dataLayer(trackContinueLogin())
      // nothing else to do: login(...) will redirect automatically on success
      return
    }

    const formErrors: Partial<Errors> = {}
    const systemErrors: LoginError[] = []

    for (const backendValidationError of loginResult.error) {
      switch (backendValidationError) {
        case LoginError.InvalidEmail:
          formErrors.email = getErrorMessages().EMAIL_IS_INVALID
          break
        default:
          systemErrors.push(backendValidationError)
      }
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors((state) => ({ ...state, ...formErrors }))
      dataLayer(logError('Login Error', formErrors.toString()))
    }

    if (systemErrors.length > 0) {
      props.onFailure({ ...values, errors: systemErrors })
      dataLayer(logError('Login Error', systemErrors.toString()))
    }
  }

  const onBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Without this guard, when a field is on focus and the submit button is
    // clicked, the onBlur validation may cause the height of the form to
    // change, making it so that the click lands outside of the submit button!
    // This is an issue in DGC forms at the time of this writing.
    // todo(jcejudo): create ticket for above scenario and link to it
    if (!isBlurValidationEnabled) {
      return
    }

    validateForm(e.target.name)
  }

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    // we need to cache these due to https://reactjs.org/docs/events.html#event-pooling
    const { name, value } = e.target

    setValues((state) => ({ ...state, [name]: value }))

    if (name === 'email') {
      props.onChangeEmail(value)
    }
  }

  const disableBlurValidation = () => setIsBlurValidationEnabled(false)
  const enableBlurValidation = () => setIsBlurValidationEnabled(true)

  const title = 'Westfield membership login'

  return (
    <OnboardingForm
      title={title}
      toggleLoginSignup={
        <p
          onMouseEnter={disableBlurValidation}
          onMouseLeave={enableBlurValidation}
          className={style.signUpText}
        >
          Not a member? <span>{props.renderSignUpLink(style.signUp)}</span>
        </p>
      }
      intro={<>Login to manage your Westfield membership account.</>}
      form={
        <Form onSubmit={onSubmit} noValidate>
          <Row>
            <Textfield
              classNames={{ wrapper: style.emailField }}
              innerRef={emailInput}
              type="email"
              label="Email"
              name="email"
              id="email"
              value={values.email}
              inputMode="email"
              autoComplete="email"
              errorMessage={errors.email}
              isHorizontal={false}
              onChange={onChange}
              onBlur={onBlur}
            />
          </Row>

          <Row>
            <PasswordField
              classNames={{ wrapper: style.passwordField }}
              innerRef={passwordInput}
              label="Password"
              name="password"
              id="password"
              value={values.password}
              preview
              errorMessage={errors.password}
              onChange={onChange}
              onBlur={onBlur}
            />
          </Row>

          <Row>
            <div
              data-testid="submit-button-wrapper"
              onMouseEnter={disableBlurValidation}
              onMouseLeave={enableBlurValidation}
              className={style.submitButtonWrapper}
            >
              <Button
                buttonType="submit"
                loading={isLoggingIn}
                disabled={isLoggingIn}
                className={style.submitButton}
              >
                Continue
              </Button>
            </div>
          </Row>
        </Form>
      }
      socialLogins={
        <SocialButtons
          className={style.socialButtons}
          appleLogin={props.appleLogin}
          googleLogin={props.googleLogin}
        />
      }
      outro={
        <>
          <div className={style.forgotPasswordSection}>
            {props.renderForgotPasswordLink(style.forgotPasswordLink)}
          </div>

          <div className={style.createPasswordButtonRow}>
            <div className={style.createPasswordDescription}>No password?</div>
            <div
              onMouseEnter={disableBlurValidation}
              onMouseLeave={enableBlurValidation}
            >
              {props.renderCreateNewPasswordLink(style.forgotPasswordLink)}
            </div>
          </div>
        </>
      }
      footer={
        <Paragraph className={style.terms}>
          By continuing, you agree to our{' '}
          <span
            onMouseEnter={disableBlurValidation}
            onMouseLeave={enableBlurValidation}
          >
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${consumerUrl}/terms-and-conditions`}
            >
              Terms and Conditions
            </a>
          </span>{' '}
          and{' '}
          <span
            onMouseEnter={disableBlurValidation}
            onMouseLeave={enableBlurValidation}
          >
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${consumerUrl}/privacy-policy`}
            >
              Privacy Statement
            </a>
          </span>
        </Paragraph>
      }
    />
  )
}

export default LoginForm
